import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useBusinessStore } from '../store/useBusinessStore';
import { ShieldAlert, Activity, DollarSign, Database, Check, Users, CalendarClock, Sparkles, Moon } from 'lucide-react';
import { useFinances } from '../hooks/useFinances';
import { useCustomers } from '../hooks/useCustomers';
import { crmSummary } from '../utils/customers';

export default function BusinessDashboard() {
  const { masterDb, activeBusinessId } = useBusinessStore();
  const activeBusiness = masterDb.find(b => String(b.id) === String(activeBusinessId));
  const { stats } = useFinances();
  const { customers, statsByKey, tableMissing } = useCustomers();
  const crm = useMemo(() => crmSummary(customers, statsByKey), [customers, statsByKey]);

  const [receiptUrl, setReceiptUrl] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  if (!activeBusiness) {
    return <div>Cargando dashboard del negocio...</div>;
  }

  const handleSendReceipt = () => {
    if (!receiptUrl) return alert("Por favor, ingresa el enlace a tu comprobante.");
    try {
      new URL(receiptUrl);
      setIsSubmitted(true);
      // alert("Comprobante enviado exitosamente.");
    } catch (e) {
      alert("Por favor, ingresa una URL válida (ej. https://...).");
    }
  };

  const isPendingReview = activeBusiness.payment_status === 'pendiente' || isSubmitted;

  return (
    <div className="space-y-6">
      {activeBusiness.payment_status !== 'aprobado' && (
        <div className="bg-amber-500/10 border border-amber-500/20 p-6 rounded-2xl flex flex-col gap-3 animate-fadeIn">
          <h3 className="text-amber-400 font-bold text-lg flex items-center gap-2">
            <ShieldAlert size={20} /> 
            {isPendingReview ? 'Pago en Revisión' : 'Suscripción Inactiva / Pago Requerido'}
          </h3>
          <p className="text-sm text-amber-200/80">
            {isPendingReview 
              ? 'Hemos recibido tu comprobante y lo estamos verificando. Pronto activaremos tu cuenta.' 
              : 'Para continuar usando los servicios de Keyla CRM (incluido el Bot de WhatsApp), por favor sube el comprobante de tu pago.'}
          </p>
          
          <div className="flex gap-2 max-w-lg mt-2">
            <input 
              type="url"
              placeholder="Pega aquí el enlace a tu comprobante (URL)..."
              className="flex-1 bg-seryi-bg-dark border border-amber-500/20 rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-amber-400 disabled:opacity-50"
              value={receiptUrl}
              onChange={(e) => setReceiptUrl(e.target.value)}
              disabled={isPendingReview}
            />
            <button 
              onClick={handleSendReceipt}
              disabled={isPendingReview}
              className="bg-amber-500 hover:bg-amber-400 disabled:bg-amber-500/50 disabled:cursor-not-allowed text-seryi-bg-dark font-bold px-4 py-2.5 rounded-xl text-xs transition-colors shrink-0 shadow-lg"
            >
              {isPendingReview ? 'Comprobante Enviado' : 'Enviar Comprobante'}
            </button>
          </div>
        </div>
      )}

      {/* HEADER WELCOME */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-black text-seryi-heading">Bienvenido, {activeBusiness.nombre_negocio}</h2>
          <p className="text-sm text-seryi-text">Aquí tienes el resumen de tu negocio en Keyla CRM.</p>
        </div>
      </div>

      {/* MOCK METRICS FOR V2 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-2xl space-y-2">
          <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Ingresos Totales</span>
          <p className="text-3xl font-black text-emerald-400">
            {stats ? `$${stats.totalPaid.toLocaleString()}` : 'Cargando...'}
          </p>
        </div>
        <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-2xl space-y-2">
          <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Pendientes</span>
          <p className="text-3xl font-black text-amber-400">
            {stats ? `$${stats.totalPending.toLocaleString()}` : 'Cargando...'}
          </p>
        </div>
      </div>

      {!tableMissing && (
        <section aria-label="Resumen de clientes" className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-extrabold text-seryi-heading">Tus clientes</h3>
            <Link to="/v2/clientes" className="text-xs font-bold text-seryi-green hover:underline">Ver todos</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              ['Nuevos esta semana', crm.newThisWeek, Sparkles, 'text-sky-300', '/v2/clientes', 'Personas que te escribieron por primera vez en los últimos 7 días'],
              ['Por contactar', crm.dueFollowUps, CalendarClock, 'text-rose-300', '/v2/clientes', 'Seguimientos vencidos o de hoy'],
              ['Con cita o pedido', `${crm.conversionPct}%`, Users, 'text-emerald-300', '/v2/clientes', `${crm.converted} de ${crm.total} personas ya agendaron o pidieron`],
              ['Inactivos', crm.inactivos, Moon, 'text-amber-300', '/v2/marketing', 'Sin actividad hace más de 60 días: buenos para una campaña de reactivación']
            ].map(([label, value, Icon, color, to, help]) => (
              <Link key={label} to={to} title={help} className="bg-seryi-bg border border-seryi-bg-light rounded-2xl p-4 hover:border-seryi-green/40 transition-colors">
                <div className="flex items-center justify-between text-seryi-text">
                  <span className="text-[10px] uppercase font-bold tracking-wider">{label}</span>
                  <Icon size={15} className={color} />
                </div>
                <p className={`text-2xl font-black mt-1 ${color}`}>{value}</p>
              </Link>
            ))}
          </div>
          {crm.total === 0 && (
            <p className="text-xs text-seryi-text">Aún no hay clientes guardados. Cada persona que te escriba por WhatsApp, agende o pida aparecerá aquí.</p>
          )}
        </section>
      )}
    </div>
  );
}
